import React from 'react'
import ListItem from '@material-ui/core/ListItem'
import List from '@material-ui/core/List'
import CircularProgress from '@material-ui/core/CircularProgress'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import { useTranslation } from 'react-i18next'

import { useParcelNotes } from '@graniot/common/adapters/note'
import FormNote from './FormNote'
import Note from './Note'
import { useAuth } from '../../../../context/AuthContext'

const useStyles = makeStyles((theme) => ({
	listItem: {
		padding: theme.spacing(1),
		[theme.breakpoints.down('md')]: {
			padding: theme.spacing(0.5),
		},
		[theme.breakpoints.down('sm')]: {
			padding: theme.spacing(0.25),
		},
	},
	list: {
		padding: 0,
		[theme.breakpoints.down('md')]: {
			padding: 0,
		},
	},
	downloadButton: {
		padding: theme.spacing(1),
	},
}))

export default function NotesList({
	parcel,
	layers,
	selectedResolution,
	maxCC,
	setMaxCC,
	from_date,
	toDate,
	isLoadingParcelFISHistory,
	dataParcelFISHistory,
	errorParcelFISHistory,
	refetchParcelFISHistory,
	selectedLayer,
	handleSelectedLayer,
}) {
	const classes = useStyles()
	const { user } = useAuth()
	const { t } = useTranslation()

	const { refetch, data, isLoading } = useParcelNotes(parcel.id, user)

	React.useEffect(() => {
		if (!data) refetch()
	}, [])

	const notesToGeoJSON = (notes) => {
		const features = []
		notes.forEach((note) => {
			if (!note.geom || !note.geometry || note.geometry.length === 0) return
			note.geometry.forEach((geom) => {
				features.push({
					type: 'Feature',
					geometry: { type: geom.type, coordinates: geom.coordinates },
					properties: {
						note_id: note.id,
						user: note.user?.username,
						note: note.note,
						reference_date: note.reference_date,
						created: note.created,
						modified: note.modified,
						layer_name: note.parameters?.layer_name,
						layer_date: note.parameters?.layer_date,
						activity: geom.properties?.activity,
						description: geom.properties?.description,
					},
				})
			})
		})
		return features
	}

	const downloadGeoJSON = (features, fileName) => {
		var a = document.createElement('a')
		var file = new Blob(
			[JSON.stringify({ type: 'FeatureCollection', features: features })],
			{ type: 'application/json' }
		)
		a.href = URL.createObjectURL(file)
		a.download = fileName
		a.click()
	}

	const handleDownloadNotes = () => {
		const features = notesToGeoJSON(data)
		downloadGeoJSON(features, `${parcel.properties.name}-notes.geojson`)
	}

	const hasGeoNotes = data && data.some((note) => note.geom && note.geometry?.length > 0)

	return (
		<div>
			<List className={classes.list}>
				<ListItem id="main-note-form" className={classes.listItem}>
					<FormNote
						layers={layers}
						selectedResolution={selectedResolution}
						parcel={parcel}
						refetchList={refetch}
                        maxCC={maxCC}
                        setMaxCC={setMaxCC}
                        from_date={from_date}
                        toDate={toDate}
                        isLoadingParcelFISHistory={isLoadingParcelFISHistory}
                        dataParcelFISHistory={dataParcelFISHistory}
                        errorParcelFISHistory={errorParcelFISHistory}
                        refetchParcelFISHistory={refetchParcelFISHistory}
                        selectedLayer={selectedLayer}
                        handleSelectedLayer={handleSelectedLayer}
					/>
				</ListItem>
				{isLoading && (
					<ListItem alignItems="center" id="note-list-loading" className={classes.listItem}>
						<Box
							display="flex"
							justifyContent="center"
							m={1}
							p={1}
							bgcolor="background.paper"
							style={{ width: '100%' }}
						>
							<CircularProgress />
						</Box>
					</ListItem>
				)}
				{data &&
					data.map((note, index) => (
						<ListItem key={note.id} id={`note-${index}`} className={classes.listItem}>
							<Note
								note={note}
								parcel={parcel}
								layers={layers}
								selectedResolution={selectedResolution}
								refetch={refetch}
							/>
						</ListItem>
					))}
			</List>
			{hasGeoNotes && (
				<Box display="flex" justifyContent="center" className={classes.downloadButton}>
					<Button
						variant="outlined"
						color="primary"
						style={{ width: '100%' }}
						onClick={handleDownloadNotes}
						endIcon={<CloudDownloadIcon />}
					>
						{t('Download file')}
					</Button>
				</Box>
			)}
		</div>
	)
}
